import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  IconButton,
  Button,
  Divider,
  Heading,
  useDisclosure,
  useBreakpointValue,
  useColorModeValue,
  useToast,
  Text,
  Image,
} from "@chakra-ui/react";
import { HamburgerIcon } from "@chakra-ui/icons";
import {
  Outlet,
  NavLink,
  useLocation,
  useNavigate,
  Link,
} from "react-router-dom";
import {
  FaUser,
  FaSignOutAlt,
  FaLink,
  FaFileAlt,
  FaBriefcase,
  FaTasks,
  FaCalendarAlt,
  FaUserFriends,
  FaClipboardList,
  FaCheckSquare,
  FaSitemap,
  FaHeadphones,
  FaPhotoVideo,
  FaMicrophone,
  FaVideo,
  FaExchangeAlt,
  FaGraduationCap,
  FaSun,
} from "react-icons/fa";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import DashboardHome from "./DashboardHome";
import DashboardTabGuideBanner from "./DashboardTabGuideBanner";
import { clearClientApiCache, hitBeApi } from "../../api/api";
import VoiceCreditPaymentModal from "./VoiceCreditPaymentModal";

const Dashboard = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const isNoLayoutRoute = ["/dashboard"].includes(location.pathname);
  const isOrgTreeRoute = location.pathname === "/dashboard/org-tree";
  const hideTabGuideBanner =
    location.pathname === "/dashboard/media-analyze" ||
    location.pathname === "/dashboard/exam-ai";
  const navWidth = useBreakpointValue({ base: "100%", lg: "300px" });
  const linkHoverBg = useColorModeValue("gray.200", "gray.700");
  const navBg = useColorModeValue("white", "gray.800");
  const mobileHeaderBorder = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const isLargeScreen = useBreakpointValue({ base: false, lg: true });
  const toast = useToast();
  const sidebarBorderColor = useColorModeValue("gray.200", "gray.700");
  const sidebarActiveBg = useColorModeValue("teal.500", "teal.400");
  const sidebarActiveHoverBg = useColorModeValue("teal.600", "teal.300");
  const sidebarActiveColor = "white";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const MotionVStack = motion(VStack);
  const [showVoiceCreditModal, setShowVoiceCreditModal] = useState(false);

  const toggleDrawer = () => {
    isOpen ? onClose() : onOpen();
  };

  const handleLogout = () => {
    clearClientApiCache();
    document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    toast({
      title: "Logout Success.",
      description: "Logout successful along with browser cache removed.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    navigate("/");
  };

  useEffect(() => {
    let mounted = true;
    const syncUser = async () => {
      try {
        const response = await hitBeApi("user", "GET");
        if (!mounted) return;
        const userDetails = response?.data?.userDetails;
        if (userDetails) {
          dispatch({ type: "SET_USER", value: userDetails });
        }
      } catch {
        // user sync is best-effort
      }
    };
    syncUser();
    return () => {
      mounted = false;
    };
  }, [dispatch]);

  useEffect(() => {
    const onCreditsExhausted = () => setShowVoiceCreditModal(true);
    window.addEventListener("manage360:ai-credits-exhausted", onCreditsExhausted);
    window.addEventListener(
      "manage360:voice-credits-exhausted",
      onCreditsExhausted
    );
    return () => {
      window.removeEventListener(
        "manage360:ai-credits-exhausted",
        onCreditsExhausted
      );
      window.removeEventListener(
        "manage360:voice-credits-exhausted",
        onCreditsExhausted
      );
    };
  }, []);

  /** Routes are under `/dashboard/...` (NavLink `to` is relative to parent). */
  const isActive = (segment) =>
    location.pathname === `/dashboard/${segment}`;

  const NavLinks = ({ placement = "drawer", omitLogout = false }) => {
    const isSidebar = placement === "sidebar";

    const baseLink = {
      width: "full",
      justifyContent: "flex-start",
      borderRadius: "lg",
      size: isSidebar ? "lg" : "md",
      colorScheme: "teal",
      onClick: isSidebar ? undefined : toggleDrawer,
    };

    const inactiveGhost = {
      variant: "ghost",
      colorScheme: "teal",
      _hover: { bg: linkHoverBg },
    };

    /** Desktop: single-line labels; drawer: taller touch rows + wrapping for long strings. */
    const layoutProps = isSidebar
      ? {
          whiteSpace: "nowrap",
          wordBreak: "normal",
          minH: "48px",
          py: 2,
        }
      : {
          whiteSpace: "normal",
          wordBreak: "break-word",
          h: "auto",
          minH: "44px",
          py: 2,
        };

    const pillFor = (segment) => {
      const active = isActive(segment);
      return {
        ...baseLink,
        ...layoutProps,
        ...inactiveGhost,
        ...(active
          ? {
              variant: "solid",
              bg: sidebarActiveBg,
              color: sidebarActiveColor,
              _hover: { bg: sidebarActiveHoverBg, color: sidebarActiveColor },
              ...(isSidebar ? { fontWeight: "semibold" } : {}),
            }
          : {}),
      };
    };

    const logoutProps = {
      ...baseLink,
      ...layoutProps,
      variant: "outline",
      colorScheme: "red",
      borderWidth: "1px",
      onClick: handleLogout,
    };

    return (
      <>
        <NavLink to="profile" exact="true">
          <Button leftIcon={<FaUser />} {...pillFor("profile")}>
            Profile
          </Button>
        </NavLink>

        <NavLink to="screen-listen" exact="true">
          <Button leftIcon={<FaHeadphones />} {...pillFor("screen-listen")}>
            Screen AI
          </Button>
        </NavLink>

        <NavLink to="media-analyze" exact="true">
          <Button leftIcon={<FaPhotoVideo />} {...pillFor("media-analyze")}>
            Media AI
          </Button>
        </NavLink>

        <NavLink to="voice-analyse" exact="true">
          <Button leftIcon={<FaMicrophone />} {...pillFor("voice-analyse")}>
            Audio AI
          </Button>
        </NavLink>

        <NavLink to="video-ai" exact="true">
          <Button leftIcon={<FaVideo />} {...pillFor("video-ai")}>
            Video AI
          </Button>
        </NavLink>

        <NavLink to="transfer-ai" exact="true">
          <Button leftIcon={<FaExchangeAlt />} {...pillFor("transfer-ai")}>
            Transfer AI
          </Button>
        </NavLink>

        <NavLink to="exam-ai" exact="true">
          <Button leftIcon={<FaGraduationCap />} {...pillFor("exam-ai")}>
            Exam AI
          </Button>
        </NavLink>

        <NavLink to="day-life" exact="true">
          <Button leftIcon={<FaSun />} {...pillFor("day-life")}>
            DayLife AI
          </Button>
        </NavLink>

        <NavLink to="notes" exact="true">
          <Button leftIcon={<FaLink />} {...pillFor("notes")}>
            Notes
          </Button>
        </NavLink>

        <NavLink to="files" exact="true">
          <Button leftIcon={<FaFileAlt />} {...pillFor("files")}>
            File
          </Button>
        </NavLink>

        <NavLink to="portfolio" exact="true">
          <Button leftIcon={<FaBriefcase />} {...pillFor("portfolio")}>
            Portfolio AI
          </Button>
        </NavLink>

        <NavLink to="project" exact="true">
          <Button leftIcon={<FaClipboardList />} {...pillFor("project")}>
            Project AI
          </Button>
        </NavLink>

        <NavLink to="org-tree" exact="true">
          <Button leftIcon={<FaSitemap />} {...pillFor("org-tree")}>
            My Org
          </Button>
        </NavLink>

        <NavLink to="todos" exact="true">
          <Button leftIcon={<FaCheckSquare />} {...pillFor("todos")}>
            Todos AI
          </Button>
        </NavLink>

        <NavLink to="daily-chore" exact="true">
          <Button leftIcon={<FaTasks />} {...pillFor("daily-chore")}>
            Daily Chore AI
          </Button>
        </NavLink>

        <NavLink to="calendar-events" exact="true">
          <Button leftIcon={<FaCalendarAlt />} {...pillFor("calendar-events")}>
            Calendar Events
          </Button>
        </NavLink>

        <NavLink to="splitwise" exact="true">
          <Button leftIcon={<FaUserFriends />} {...pillFor("splitwise")}>
            Splitwise Groups
          </Button>
        </NavLink>

        {!omitLogout ? (
          <Button leftIcon={<FaSignOutAlt />} {...logoutProps}>
            Logout
          </Button>
        ) : null}
      </>
    );
  };

  const mainAreaProps = {
    flex: "1",
    w: { base: "100%", lg: "calc(100% - 300px)" },
    maxW: { base: "100vw", lg: "calc(100vw - 300px)" },
    pt: {
      base:
        "max(5.25rem, calc(3.5rem + env(safe-area-inset-top, 0px) + 1.25rem))",
      md: "4.5rem",
      lg: 3,
    },
    pb: {
      base: "calc(1rem + env(safe-area-inset-bottom, 0px))",
      md: "calc(1.25rem + env(safe-area-inset-bottom, 0px))",
      lg: "calc(0.75rem + env(safe-area-inset-bottom, 0px))",
    },
    px: 0,
    position: "absolute",
    left: { base: 0, lg: "300px" },
    right: 0,
    top: 0,
    bottom: { base: 0, lg: 0 },
    zIndex: 0,
    overflowY: isOrgTreeRoute ? "hidden" : "auto",
    overflowX: "hidden",
    sx: {
      WebkitOverflowScrolling: "touch",
      overscrollBehaviorY: "contain",
    },
  };

  return (
    <HStack
      w="100%"
      maxW="100vw"
      minH="100vh"
      align="start"
      spacing={0}
      bg={pageBg}
      overflow="hidden"
      position="relative"
    >
      {isLargeScreen ? (
        <MotionVStack
          as="nav"
          align="stretch"
          spacing={0}
          pt={5}
          px={4}
          pb={4}
          bg={navBg}
          color={textColor}
          minW={navWidth}
          w={navWidth}
          borderRight="1px"
          borderColor={sidebarBorderColor}
          minHeight="100vh"
          height="100vh"
          boxShadow={{ base: "none", lg: "sm" }}
          display="flex"
          flexDirection="column"
          flexShrink={0}
          initial={{ x: -250 }}
          animate={{ x: 0 }}
          transition={{ type: "spring", stiffness: 100 }}
          position="fixed"
          left={0}
          top={0}
          zIndex={50}
          overflow="hidden"
        >
          <Link to="/" aria-label="Manage360 home">
            <HStack spacing={3} cursor="pointer" py={1} pb={4}>
              <Image src="/logo.png" alt="" boxSize="40px" draggable={false} />
              <Heading as="span" fontSize="lg" fontWeight="bold" noOfLines={1}>
                Manage360.me
              </Heading>
            </HStack>
          </Link>
          <Divider mb={3} />
          <VStack
            align="stretch"
            spacing={2}
            flex="1"
            minH={0}
            overflowY="auto"
            overflowX="hidden"
            sx={{ scrollbarGutter: "stable" }}
            pb={2}
          >
            <NavLinks placement="sidebar" omitLogout />
          </VStack>
          <Divider borderColor={sidebarBorderColor} />
          <Box flexShrink={0} pt={3}>
            <Button
              leftIcon={<FaSignOutAlt />}
              width="full"
              justifyContent="flex-start"
              borderRadius="lg"
              size="lg"
              variant="outline"
              colorScheme="red"
              borderWidth="1px"
              minH="48px"
              py={2}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>
        </MotionVStack>
      ) : (
        <>
          {/*
            Fixed bar + z-index: the scroll area below uses position:absolute and was
            painting above the old absolute-only hamburger, blocking taps on mobile.
          */}
          <Box
            as="header"
            position="fixed"
            top={0}
            left={0}
            right={0}
            zIndex={100}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={3}
            minH="56px"
            px={3}
            pr="max(12px, env(safe-area-inset-right, 0px))"
            py={2}
            pt="max(0.5rem, env(safe-area-inset-top, 0px))"
            bg={navBg}
            borderBottomWidth="1px"
            borderBottomColor={mobileHeaderBorder}
            boxShadow="sm"
          >
            <IconButton
              aria-label="Open menu"
              icon={<HamburgerIcon />}
              onClick={toggleDrawer}
              variant="outline"
              colorScheme="gray"
              size="md"
              flexShrink={0}
            />
            <Text
              as="span"
              fontSize="lg"
              fontWeight="bold"
              color={textColor}
              noOfLines={1}
              textAlign="right"
              flex="1"
              minW={0}
            >
              Manage360.me
            </Text>
          </Box>
          <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
            <DrawerOverlay />
            <DrawerContent
              bg={navBg}
              maxW="min(100vw, 340px)"
              sx={{
                pb: "calc(0.75rem + env(safe-area-inset-bottom, 0px))",
              }}
            >
              <DrawerCloseButton />
              <DrawerHeader borderBottomWidth="0" py={4}>
                Manage360.me
              </DrawerHeader>
              <DrawerBody px={3} pb={4}>
                <VStack align="stretch" spacing={{ base: 1, md: 2 }}>
                  <NavLinks placement="drawer" />
                </VStack>
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        </>
      )}

      <Box {...mainAreaProps}>
        {!isNoLayoutRoute ? (
          isOrgTreeRoute ? (
            <Box
              w="100%"
              h="100%"
              minH="100%"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              flex="1"
            >
              <Outlet />
            </Box>
          ) : (
            <Box px={{ base: 3, sm: 4, md: 5 }} maxW="100%" mx="auto">
              {!hideTabGuideBanner ? <DashboardTabGuideBanner /> : null}
              <Outlet />
            </Box>
          )
        ) : (
          <DashboardHome />
        )}
      </Box>
      <VoiceCreditPaymentModal
        isOpen={showVoiceCreditModal}
        onClose={() => setShowVoiceCreditModal(false)}
        variant="exhausted"
      />
    </HStack>
  );
};

export default Dashboard;
